import { Suspense, lazy, useEffect, useRef } from "react";
import { lazyRetry } from "@/lib/lazyRetry";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { PageSkeleton } from "@/components/ui/list-skeleton";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import { SystemStatusRoute } from "./components/SystemStatusRoute";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { DriverRouteGuard } from "./components/DriverRouteGuard";
import { DailyCheckAccessGate } from "./components/DailyCheckAccessGate";
import { SupportScopedRoute } from "./components/support/SupportScopedRoute";
import SAIndexRoute from "./pages/sa/mobile/SAIndexRoute";
import { OfflineIndicator } from "./components/OfflineIndicator";
import { BackendStatusBanner } from "./components/BackendStatusBanner";
import { BackendHealthProvider } from "./contexts/BackendHealthContext";
import { AuthProvider } from "./contexts/AuthContext";
import { SupportSessionProvider } from "./contexts/SupportSessionContext";
import { SupportModeBanner } from "./components/support/SupportModeBanner";
import { StartupGate } from "./components/StartupGate";
import { AppUpdateGate } from "./components/AppUpdateGate";
import { NativeStartupOverlay } from "./components/NativeStartupOverlay";
// LocationReminderBanner is now rendered inside job screens only
import { SessionExpiredBanner } from "./components/SessionExpiredBanner";
import { useAppleLoginSync } from "./hooks/useAppleLoginSync";
import { DemoBanner } from "./components/DemoBanner";
import { DemoEnvironmentBadge } from "./components/demo/DemoEnvironmentBadge";
import { MobileAppBanner } from "./components/MobileAppBanner";
import { SmartDeepLink } from "./components/SmartDeepLink";
import { setupDeepLinks, isNative } from "./lib/capacitor";
import { supabase } from "./integrations/supabase/client";
import { initNativePushListeners, consumePendingDeepLink } from "./lib/nativePushNotifications";
import { ScrollToTopOnNavigate } from "./components/ScrollToTopOnNavigate";
import { PageViewTracker } from "./components/PageViewTracker";
import { UtmCapture } from "./components/UtmCapture";
import { useAndroidBackButton } from "./hooks/useAndroidBackButton";
import { DriverLocationTracker } from "./components/DriverLocationTracker";
import { NativeErrorBoundary } from "./components/NativeErrorBoundary";
import { LocationDebugPanel } from "./components/debug/LocationDebugPanel";
import { queryClient } from "./lib/queryClient";
import { GlobalCommandBar } from "./components/command/GlobalCommandBar";

// Eager load critical paths
import Landing from "./pages/Landing";
import MarketingLayout from "./components/marketing/MarketingLayout";
import { SEO_PAGES } from "./content/seoPages";
import { AppFooterLayout } from "./components/AppFooterLayout";

import Auth from "./pages/Auth";
import Index from "./pages/Index";

const StartTrial = lazyRetry(() => import("./pages/StartTrial"), "StartTrial");
const FacebookLandingPage = lazyRetry(() => import("./pages/marketing/FacebookLandingPage"), "FacebookLandingPage");
const ComparePocpodOvmProPage = lazyRetry(() => import("./pages/marketing/ComparePocpodOvmProPage"), "ComparePocpodOvmProPage");
const SeoContentPage = lazyRetry(() => import("./components/marketing/SeoContentPage"), "SeoContentPage");
const FeaturesPage = lazyRetry(() => import("./pages/marketing/FeaturesPage"), "FeaturesPage");
const OfflineModePage = lazyRetry(() => import("./pages/marketing/OfflineModePage"), "OfflineModePage");
const ProofOfDeliveryPage = lazyRetry(() => import("./pages/marketing/ProofOfDeliveryPage"), "ProofOfDeliveryPage");
const EpodSoftwarePage = lazyRetry(() => import("./pages/marketing/EpodSoftwarePage"), "EpodSoftwarePage");
const VehicleInspectionPage = lazyRetry(() => import("./pages/marketing/VehicleInspectionPage"), "VehicleInspectionPage");
const VehicleTransportSoftwarePage = lazyRetry(() => import("./pages/marketing/VehicleTransportSoftwarePage"), "VehicleTransportSoftwarePage");
const SolutionsPage = lazyRetry(() => import("./pages/marketing/SolutionsPage"), "SolutionsPage");
const PricingPage = lazyRetry(() => import("./pages/marketing/PricingPage"), "PricingPage");
const IndustriesPage = lazyRetry(() => import("./pages/marketing/IndustriesPage"), "IndustriesPage");
const IntegrationsPage = lazyRetry(() => import("./pages/marketing/IntegrationsPage"), "IntegrationsPage");
const ResourcesPage = lazyRetry(() => import("./pages/marketing/ResourcesPage"), "ResourcesPage");
const HelpCentrePage = lazyRetry(() => import("./pages/marketing/HelpCentrePage"), "HelpCentrePage");
const AboutPage = lazyRetry(() => import("./pages/marketing/AboutPage"), "AboutPage");
const AboutTransportMatePage = lazyRetry(() => import("./pages/marketing/AboutTransportMatePage"), "AboutTransportMatePage");
const ContactPage = lazyRetry(() => import("./pages/marketing/ContactPage"), "ContactPage");
const BlogIndexPage = lazyRetry(() => import("./pages/marketing/BlogIndexPage"), "BlogIndexPage");
const BlogPostPage = lazyRetry(() => import("./pages/marketing/BlogPostPage"), "BlogPostPage");


// Lazy load all other pages for better initial bundle size
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"), "ResetPassword");
const PrivacyPolicy = lazyRetry(() => import("./pages/PrivacyPolicy"), "PrivacyPolicy");
const Terms = lazyRetry(() => import("./pages/Terms"), "Terms");
const DeleteAccount = lazyRetry(() => import("./pages/DeleteAccount"), "DeleteAccount");
const Analytics = lazyRetry(() => import("./pages/Analytics"), "Analytics");
const Schedule = lazyRetry(() => import("./pages/Schedule"), "Schedule");
const Customers = lazyRetry(() => import("./pages/Customers"), "Customers");
const CustomerProfile = lazyRetry(() => import("./pages/CustomerProfile"), "CustomerProfile");
const CreateJob = lazyRetry(() => import("./pages/CreateJob"), "CreateJob");

const CreateDriverJob = lazyRetry(() => import("./pages/CreateDriverJob"), "CreateDriverJob");
const DriverManagement = lazyRetry(() => import("./pages/DriverManagement"), "DriverManagement");
const DriverTracking = lazyRetry(() => import("./pages/DriverTracking"), "DriverTracking");
const DriverMessages = lazyRetry(() => import("./pages/DriverMessages"), "DriverMessages");
const DriverMessageLog = lazyRetry(() => import("./pages/DriverMessageLog"), "DriverMessageLog");
const DriverMessagesView = lazyRetry(() => import("./pages/DriverMessagesView"), "DriverMessagesView");
const MapsStatusDebug = lazyRetry(() => import("./pages/MapsStatusDebug"), "MapsStatusDebug");
const Invoicing = lazyRetry(() => import("./pages/Invoicing"), "Invoicing");
const ManageUsers = lazyRetry(() => import("./pages/ManageUsers"), "ManageUsers");
const PendingJobs = lazyRetry(() => import("./pages/PendingJobs"), "PendingJobs");
const JobsHub = lazyRetry(() => import("./pages/JobsHub"), "JobsHub");
const OperationsCentre = lazyRetry(() => import("./pages/OperationsCentre"), "OperationsCentre");
const AutomationCentre = lazyRetry(() => import("./pages/AutomationCentre"), "AutomationCentre");
const PreviousJobs = lazyRetry(() => import("./pages/PreviousJobs"), "PreviousJobs");

const Vehicles = lazyRetry(() => import("./pages/Vehicles"), "Vehicles");
const Settings = lazyRetry(() => import("./pages/Settings"), "Settings");
const Subscriptions = lazyRetry(() => import("./pages/Subscriptions"), "Subscriptions");
const SubscriptionStatus = lazyRetry(() => import("./pages/SubscriptionStatus"), "SubscriptionStatus");
const Subscribe = lazyRetry(() => import("./pages/Subscribe"), "Subscribe");
const ChangePlan = lazyRetry(() => import("./pages/ChangePlan"), "ChangePlan");
const PaymentSuccess = lazyRetry(() => import("./pages/PaymentSuccess"), "PaymentSuccess");
const DownloadPage = lazyRetry(() => import("./pages/Download"), "Download");
const ResumeJob = lazyRetry(() => import("./pages/ResumeJob"), "ResumeJob");
const JobDetails = lazyRetry(() => import("./pages/JobDetails"), "JobDetails");
const JobPrintView = lazyRetry(() => import("./pages/JobPrintView"), "JobPrintView");
const InvoiceViewer = lazyRetry(() => import("./pages/InvoiceViewer"), "InvoiceViewer");
const AppraisalViewer = lazyRetry(() => import("./pages/AppraisalViewer"), "AppraisalViewer");
const TrackDelivery = lazyRetry(() => import("./pages/TrackDelivery"), "TrackDelivery");
const NotFound = lazyRetry(() => import("./pages/NotFound"), "NotFound");
const CreateAdminUser = lazyRetry(() => import("./pages/CreateAdminUser"), "CreateAdminUser");
const ReadyToInvoice = lazyRetry(() => import("./pages/ReadyToInvoice"), "ReadyToInvoice");
const CompanyManagement = lazyRetry(() => import("./pages/CompanyManagement"), "CompanyManagement");
const CompanyDetails = lazyRetry(() => import("./pages/CompanyDetails"), "CompanyDetails");
const LinkSubscription = lazyRetry(() => import("./pages/LinkSubscription"), "LinkSubscription");
const DriverSettings = lazyRetry(() => import("./pages/DriverSettings"), "DriverSettings");
const HelpGuides = lazyRetry(() => import("./pages/HelpGuides"), "HelpGuides");
const SetupCentre = lazyRetry(() => import("./pages/SetupCentre"), "SetupCentre");
const VehicleDailyChecks = lazyRetry(() => import("./pages/VehicleDailyChecks"), "VehicleDailyChecks");
const QuickQuote = lazyRetry(() => import("./pages/QuickQuote"), "QuickQuote");
const QuoteBuilder = lazyRetry(() => import("./pages/QuoteBuilder"), "QuoteBuilder");
const SavedQuotes = lazyRetry(() => import("./pages/SavedQuotes"), "SavedQuotes");

const NativeShortcutsDashboard = lazyRetry(() => import("./components/NativeShortcutsDashboard"), "NativeShortcutsDashboard");
const AnchorDebugOverlay = lazyRetry(() => import("./pages/AnchorDebugOverlay"), "AnchorDebugOverlay");
const AnchorCalibration = lazyRetry(() => import("./pages/AnchorCalibration"), "AnchorCalibration");
const SystemStatus = lazyRetry(() => import("./pages/SystemStatus"), "SystemStatus");
const Feedback = lazyRetry(() => import("./pages/Feedback"), "Feedback");
const ReportProblem = lazyRetry(() => import("./pages/ReportProblem"), "ReportProblem");
const SignaturePage = lazyRetry(() => import("./pages/SignaturePage"), "SignaturePage");
const PublicQuote = lazyRetry(() => import("./pages/PublicQuote"), "PublicQuote");
const OfflineDriverJobs = lazyRetry(() => import("./pages/OfflineDriverJobs"), "OfflineDriverJobs");
const OfflineJobDetail = lazyRetry(() => import("./pages/OfflineJobDetail"), "OfflineJobDetail");
const More = lazyRetry(() => import("./pages/More"), "More");
const PendingUploads = lazyRetry(() => import("./pages/PendingUploads"), "PendingUploads");
const Subcontractors = lazyRetry(() => import("./pages/Subcontractors"), "Subcontractors");
const SubcontractorDetail = lazyRetry(() => import("./pages/SubcontractorDetail"), "SubcontractorDetail");
const AcceptInvite = lazyRetry(() => import("./pages/AcceptInvite"), "AcceptInvite");
const IOSPlans = lazyRetry(() => import("./pages/IOSPlans"), "IOSPlans");
const IOSPostPurchaseSetup = lazyRetry(() => import("./pages/IOSPostPurchaseSetup"), "IOSPostPurchaseSetup");
const Expenses = lazyRetry(() => import("./pages/Expenses"), "Expenses");

// New Super Admin layout and pages
const SuperAdminLayout = lazyRetry(() => import("./layouts/SuperAdminLayout"), "SuperAdminLayout");
const SACEODashboard = lazyRetry(() => import("./pages/sa/SACEODashboard"), "SACEODashboard");
const SAMobileShell = lazyRetry(() => import("./pages/sa/mobile/SAMobileShell"), "SAMobileShell");
const SADashboard = lazyRetry(() => import("./pages/sa/SADashboard"), "SADashboard");
const SALiveTracking = lazyRetry(() => import("./pages/sa/SALiveTracking"), "SALiveTracking");
const SAControlTower = lazyRetry(() => import("./pages/sa/SAControlTower"), "SAControlTower");
// Operations pages
const SACompanies = lazyRetry(() => import("./pages/sa/SACompanies"), "SACompanies");
const SACompanyDetail = lazyRetry(() => import("./pages/sa/SACompanyDetail"), "SACompanyDetail");
const SACompanyIntelligence = lazyRetry(() => import("./pages/sa/SACompanyIntelligence"), "SACompanyIntelligence");
const SAExecutiveIntelligence = lazyRetry(() => import("./pages/sa/SAExecutiveIntelligence"), "SAExecutiveIntelligence");
const SAVehicles = lazyRetry(() => import("./pages/sa/SAVehicles"), "SAVehicles");
const SAUsers = lazyRetry(() => import("./pages/sa/SAUsers"), "SAUsers");
const SADrivers = lazyRetry(() => import("./pages/sa/SADrivers"), "SADrivers");
const SAJobs = lazyRetry(() => import("./pages/sa/SAJobs"), "SAJobs");
const SAInvoices = lazyRetry(() => import("./pages/sa/SAInvoices"), "SAInvoices");
const SABatchInvoicing = lazyRetry(() => import("./pages/sa/SAInvoices"), "SABatchInvoicing"); // redirects to unified invoices page
const SAProblemJobs = lazyRetry(() => import("./pages/sa/SAProblemJobs"), "SAProblemJobs");
const SAAppraisals = lazyRetry(() => import("./pages/sa/SAAppraisals"), "SAAppraisals");
const SADailyChecks = lazyRetry(() => import("./pages/sa/SADailyChecks"), "SADailyChecks");
// Platform pages
const SAIntegrations = lazyRetry(() => import("./pages/sa/SAIntegrations"), "SAIntegrations");
// FleetHub (MOCK-only, development tenant only; not linked from navigation)
const FleetHubDev = lazyRetry(() => import("./pages/FleetHubDev"), "FleetHubDev");
const FleetHubJobsPreview = lazyRetry(() => import("./pages/FleetHubJobsPreview"), "FleetHubJobsPreview");
const FleetHubJobs = lazyRetry(() => import("./pages/FleetHubJobs"), "FleetHubJobs");
const SACommunications = lazyRetry(() => import("./pages/sa/SACommunications"), "SACommunications");
const SAOutbox = lazyRetry(() => import("./pages/sa/SAOutbox"), "SAOutbox");
const SASystemHealth = lazyRetry(() => import("./pages/sa/SASystemHealth"), "SASystemHealth");
const SAObservability = lazyRetry(() => import("./pages/sa/SAObservability"), "SAObservability");
const SAStorage = lazyRetry(() => import("./pages/sa/SAStorageCosts"), "SAStorage");
const SASecurity = lazyRetry(() => import("./pages/sa/SASecurity"), "SASecurity");
// Intelligence pages
const SAAnalytics = lazyRetry(() => import("./pages/sa/SAAnalytics"), "SAAnalytics");
const SARevenue = lazyRetry(() => import("./pages/sa/SARevenue"), "SARevenue");
const SAGrowth = lazyRetry(() => import("./pages/sa/SAGrowth"), "SAGrowth");
const SAChurnRisk = lazyRetry(() => import("./pages/sa/SAChurnRisk"), "SAChurnRisk");
const SACancellations = lazyRetry(() => import("./pages/sa/SACancellations"), "SACancellations");
const SAProfitLoss = lazyRetry(() => import("./pages/sa/SAProfitLoss"), "SAProfitLoss");
// AI Knowledge Engine
const KnowledgeLayout = lazyRetry(() => import("./pages/sa/knowledge/KnowledgeLayout"), "KnowledgeLayout");
const KnowledgeDashboard = lazyRetry(() => import("./pages/sa/knowledge/KnowledgeDashboard"), "KnowledgeDashboard");
const KnowledgeTopics = lazyRetry(() => import("./pages/sa/knowledge/KnowledgeTopics"), "KnowledgeTopics");
const KnowledgeTopicEditor = lazyRetry(() => import("./pages/sa/knowledge/KnowledgeTopicEditor"), "KnowledgeTopicEditor");
const KnowledgeEntities = lazyRetry(() => import("./pages/sa/knowledge/KnowledgeEntities"), "KnowledgeEntities");
const KnowledgeEntityEditor = lazyRetry(() => import("./pages/sa/knowledge/KnowledgeEntityEditor"), "KnowledgeEntityEditor");
const KnowledgeFaqs = lazyRetry(() => import("./pages/sa/knowledge/KnowledgeFaqs"), "KnowledgeFaqs");
const KnowledgeAssets = lazyRetry(() => import("./pages/sa/knowledge/KnowledgeAssets"), "KnowledgeAssets");
const ContentComposer = lazyRetry(() => import("./pages/sa/knowledge/ContentComposer"), "ContentComposer");
const ContentComposerEditor = lazyRetry(() => import("./pages/sa/knowledge/ContentComposerEditor"), "ContentComposerEditor");
const ContentStrategist = lazyRetry(() => import("./pages/sa/knowledge/ContentStrategist"), "ContentStrategist");
const ResearchAgent = lazyRetry(() => import("./pages/sa/knowledge/ResearchAgent"), "ResearchAgent");
const ResearchRunReview = lazyRetry(() => import("./pages/sa/knowledge/ResearchRunReview"), "ResearchRunReview");
const KnowledgePages = lazyRetry(() => import("./pages/sa/knowledge/KnowledgePages"), "KnowledgePages");
const KnowledgeGraph = lazyRetry(() => import("./pages/sa/knowledge/KnowledgeGraph"), "KnowledgeGraph");
const KnowledgeQueue = lazyRetry(() => import("./pages/sa/knowledge/KnowledgeQueue"), "KnowledgeQueue");
const KnowledgeSettings = lazyRetry(() => import("./pages/sa/knowledge/KnowledgeSettings"), "KnowledgeSettings");
const AiProviders = lazyRetry(() => import("./pages/sa/knowledge/AiProviders"), "AiProviders");
const KnowledgeCategoriesPage = lazyRetry(
  () => import("./pages/sa/knowledge/KnowledgeTaxonomyPages").then((m) => ({ default: m.KnowledgeCategoriesPage })),
  "KnowledgeCategoriesPage"
);
const KnowledgeIndustriesPage = lazyRetry(
  () => import("./pages/sa/knowledge/KnowledgeTaxonomyPages").then((m) => ({ default: m.KnowledgeIndustriesPage })),
  "KnowledgeIndustriesPage"
);
const KnowledgeFeaturesPage = lazyRetry(
  () => import("./pages/sa/knowledge/KnowledgeTaxonomyPages").then((m) => ({ default: m.KnowledgeFeaturesPage })),
  "KnowledgeFeaturesPage"
);
const KnowledgeCompetitorsPage = lazyRetry(
  () => import("./pages/sa/knowledge/KnowledgeTaxonomyPages").then((m) => ({ default: m.KnowledgeCompetitorsPage })),
  "KnowledgeCompetitorsPage"
);
// Admin pages
const SAFeatureFlags = lazyRetry(() => import("./pages/sa/SAFeatureFlags"), "SAFeatureFlags");
const SARateLimits = lazyRetry(() => import("./pages/sa/SARateLimits"), "SARateLimits");
const SAMaintenance = lazyRetry(() => import("./pages/sa/SAMaintenance"), "SAMaintenance");
const SAProviderConfig = lazyRetry(() => import("./pages/sa/SAProviderConfig"), "SAProviderConfig");
const SAAuditLogs = lazyRetry(() => import("./pages/sa/SAAuditLogs"), "SAAuditLogs");
const SAActivityLog = lazyRetry(() => import("./pages/sa/SAActivityLog"), "SAActivityLog");
const SASessions = lazyRetry(() => import("./pages/sa/SASessions"), "SASessions");
const SASupportSessions = lazyRetry(() => import("./pages/sa/SASupportSessions"), "SASupportSessions");
const SAOperationalAlerts = lazyRetry(() => import("./pages/sa/SAOperationalAlerts"), "SAOperationalAlerts");
const SASettings = lazyRetry(() => import("./pages/sa/SASettings"), "SASettings");
const SADemo = lazyRetry(() => import("./pages/sa/SADemo"), "SADemo");

// Desktop admin wrapper
import { DesktopAdminWrapper } from "./components/desktop/DesktopAdminWrapper";

const PageLoader = () => <PageSkeleton />;

/** Native invoice deep links open the current invoice preview rather than the legacy PDF iframe. */
function InvoiceRoute() {
  const { jobId } = useParams();
  if (isNative && jobId) {
    return <Navigate to={`/invoicing?invoice=${encodeURIComponent(jobId)}`} replace />;
  }
  return <InvoiceViewer />;
}

// Deep link handler component
function DeepLinkHandler() {
  const navigate = useNavigate();
  const pushCleanup = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!isNative) return;

    // Initialize native push notification listeners
    pushCleanup.current = initNativePushListeners((path) => {
      console.log('[NativePush] Navigating to:', path);
      navigate(path);
    });

    const cleanup = setupDeepLinks(async (url) => {
      console.log('[DeepLink] Processing URL:', url);
      const normalizedUrl = url.replace('transportmate://', 'https://placeholder.com/');
      const urlObj = new URL(normalizedUrl);
      const isOwnedHttpsLink =
        urlObj.protocol !== 'https:' ||
        urlObj.hostname === 'transportmate.co.uk' ||
        urlObj.hostname === 'www.transportmate.co.uk' ||
        urlObj.hostname === 'placeholder.com';

      if (!isOwnedHttpsLink) return;
      
      // Handle OAuth callback URLs (e.g., transportmate://auth/callback#access_token=...)
      if (url.includes('auth/callback') || url.includes('access_token')) {
        const hashParams = new URLSearchParams(urlObj.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        
        if (accessToken && refreshToken) {
          await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          // Check for pending deep link from push notification
          const pendingLink = consumePendingDeepLink();
          navigate(pendingLink || '/dashboard');
          return;
        }
      }
      
      // Handle password reset URLs
      if (url.includes('reset-password')) {
        navigate(`/reset-password${urlObj.search}${urlObj.hash}`);
        return;
      }

      // Handle owned-domain Universal Links/App Links and remaining custom-scheme routes.
      navigate(`${urlObj.pathname}${urlObj.search}${urlObj.hash}`);
    });

    return () => {
      cleanup();
      pushCleanup.current?.();
    };
  }, [navigate]);

  return null;
}

// Handles Android hardware back button
function AndroidBackHandler() {
  useAndroidBackButton();
  return null;
}

// Syncs Apple IAP subscriptions on login (iOS only)
function AppleLoginSyncRunner() {
  useAppleLoginSync();
  return null;
}

// Global unhandled rejection handler moved to main.tsx boot sequence

// Removed NativeSplashWrapper - replaced by StartupGate component
const App = () => (
  <QueryClientProvider client={queryClient}>
    <BackendHealthProvider>
      <TooltipProvider>
        <OfflineIndicator />
        <BackendStatusBanner />
        <Toaster />
        <Sonner />
      <BrowserRouter>
        <AuthProvider>
        <SupportSessionProvider>
        <SupportModeBanner />
        <ScrollToTopOnNavigate />
        <PageViewTracker />
        <UtmCapture />
        <DeepLinkHandler />
        <AndroidBackHandler />
        <NativeStartupOverlay />
        <AppUpdateGate />
        {/* LocationReminderBanner is now rendered inside job screens only */}
        <SessionExpiredBanner />
        <AppleLoginSyncRunner />
        <DriverLocationTracker />
        <LocationDebugPanel />
        <DemoBanner />
        <DemoEnvironmentBadge />
        <MobileAppBanner />
        <SmartDeepLink />
        <GlobalCommandBar />
        <NativeErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route element={<MarketingLayout />}>
              <Route path="/" element={<StartupGate />} />
              <Route path="/welcome" element={<Landing />} />
              <Route path="/features" element={<Suspense fallback={<PageLoader />}><FeaturesPage /></Suspense>} />
              <Route path="/features/offline-mode" element={<Suspense fallback={<PageLoader />}><OfflineModePage /></Suspense>} />
              <Route path="/features/proof-of-delivery" element={<Suspense fallback={<PageLoader />}><ProofOfDeliveryPage /></Suspense>} />
              <Route path="/features/epod-software" element={<Suspense fallback={<PageLoader />}><EpodSoftwarePage /></Suspense>} />
              <Route path="/features/vehicle-inspection" element={<Suspense fallback={<PageLoader />}><VehicleInspectionPage /></Suspense>} />
              <Route path="/solutions" element={<Suspense fallback={<PageLoader />}><SolutionsPage /></Suspense>} />
              <Route path="/solutions/vehicle-transport-software" element={<Suspense fallback={<PageLoader />}><VehicleTransportSoftwarePage /></Suspense>} />
              <Route path="/pricing" element={<Suspense fallback={<PageLoader />}><PricingPage /></Suspense>} />
              <Route path="/industries" element={<Suspense fallback={<PageLoader />}><IndustriesPage /></Suspense>} />
              <Route path="/integrations" element={<Suspense fallback={<PageLoader />}><IntegrationsPage /></Suspense>} />
              <Route path="/resources" element={<Suspense fallback={<PageLoader />}><ResourcesPage /></Suspense>} />
              <Route path="/help-centre" element={<Suspense fallback={<PageLoader />}><HelpCentrePage /></Suspense>} />
              <Route path="/about" element={<Suspense fallback={<PageLoader />}><AboutPage /></Suspense>} />
              <Route path="/about-transportmate" element={<Suspense fallback={<PageLoader />}><AboutTransportMatePage /></Suspense>} />
              <Route path="/contact" element={<Suspense fallback={<PageLoader />}><ContactPage /></Suspense>} />
              <Route path="/blog" element={<Suspense fallback={<PageLoader />}><BlogIndexPage /></Suspense>} />
              <Route path="/blog/:slug" element={<Suspense fallback={<PageLoader />}><BlogPostPage /></Suspense>} />
              {/* Standalone SEO content pages (public marketing only) */}
              {SEO_PAGES.map((seoPage) => (
                <Route
                  key={seoPage.path}
                  path={seoPage.path}
                  element={<Suspense fallback={<PageLoader />}><SeoContentPage content={seoPage} /></Suspense>}
                />
              ))}
              <Route path="/start" element={<StartTrial />} />
              <Route path="/free-trial" element={<StartTrial />} />
              <Route path="/facebook" element={<Suspense fallback={<PageLoader />}><FacebookLandingPage /></Suspense>} />
              <Route path="/compare/transportmate-vs-pocpod" element={<Suspense fallback={<PageLoader />}><ComparePocpodOvmProPage /></Suspense>} />
              <Route path="/compare/transportmate-vs-pocpod-vs-ovm-pro" element={<Navigate to={{ pathname: "/compare/transportmate-vs-pocpod", search: window.location.search, hash: window.location.hash }} replace />} />
              <Route path="/compare/transportmate-vs-pocpod-ovm-pro" element={<Navigate to={{ pathname: "/compare/transportmate-vs-pocpod", search: window.location.search, hash: window.location.hash }} replace />} />
              <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/delete-account" element={<DeleteAccount />} />
            </Route>

            <Route path="/sa/system-status" element={<SystemStatusRoute><Suspense fallback={<PageLoader />}><SystemStatus /></Suspense></SystemStatusRoute>} />
            <Route path="/system-status" element={<SystemStatusRoute><Suspense fallback={<PageLoader />}><SystemStatus /></Suspense></SystemStatusRoute>} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route element={<AppFooterLayout />}>
              <Route path="/ios-plans" element={<Suspense fallback={<PageLoader />}><IOSPlans /></Suspense>} />
              <Route path="/ios-setup" element={<Suspense fallback={<PageLoader />}><IOSPostPurchaseSetup /></Suspense>} />
            </Route>
            <Route path="/dashboard" element={<ProtectedRoute><DailyCheckAccessGate><Index /></DailyCheckAccessGate></ProtectedRoute>} />
            {/* Print-only job sheet — deliberately outside every layout so no app chrome is printed */}
            <Route path="/job/:jobId/print" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><JobPrintView /></Suspense></ProtectedRoute>} />
            <Route path="/jobs/:jobId/print" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><JobPrintView /></Suspense></ProtectedRoute>} />
            {/* Admin-wrappable routes: get DesktopAdminLayout on desktop */}
            <Route element={<ProtectedRoute><DriverRouteGuard><DesktopAdminWrapper /></DriverRouteGuard></ProtectedRoute>}>
              <Route path="/analytics" element={<Suspense fallback={<PageLoader />}><Analytics /></Suspense>} />
              <Route path="/schedule" element={<Suspense fallback={<PageLoader />}><Schedule /></Suspense>} />
              <Route path="/customers" element={<Suspense fallback={<PageLoader />}><Customers /></Suspense>} />
              <Route path="/customers/:customerId" element={<Suspense fallback={<PageLoader />}><CustomerProfile /></Suspense>} />
              <Route path="/create-job" element={<Suspense fallback={<PageLoader />}><NativeErrorBoundary fallbackTitle="Job editor crashed"><CreateJob /></NativeErrorBoundary></Suspense>} />
              <Route path="/create-driver-job" element={<Suspense fallback={<PageLoader />}><CreateDriverJob /></Suspense>} />
              <Route path="/driver-management" element={<Suspense fallback={<PageLoader />}><DriverManagement /></Suspense>} />
              <Route path="/driver-tracking" element={<Suspense fallback={<PageLoader />}><DriverTracking /></Suspense>} />
              <Route path="/driver-messages" element={<Suspense fallback={<PageLoader />}><DriverMessages /></Suspense>} />
              <Route path="/driver-message-log" element={<Suspense fallback={<PageLoader />}><DriverMessageLog /></Suspense>} />
              <Route path="/my-messages" element={<Suspense fallback={<PageLoader />}><DriverMessagesView /></Suspense>} />
              <Route path="/maps-debug" element={<Suspense fallback={<PageLoader />}><MapsStatusDebug /></Suspense>} />
              <Route path="/invoicing" element={<Suspense fallback={<PageLoader />}><Invoicing /></Suspense>} />
              <Route path="/manage-users" element={<Suspense fallback={<PageLoader />}><ManageUsers /></Suspense>} />
              <Route path="/operations" element={<Suspense fallback={<PageLoader />}><OperationsCentre /></Suspense>} />
              <Route path="/jobs" element={<Suspense fallback={<PageLoader />}><JobsHub /></Suspense>} />
              <Route path="/pending-jobs" element={<Suspense fallback={<PageLoader />}><PendingJobs /></Suspense>} />
              <Route path="/previous-jobs" element={<Suspense fallback={<PageLoader />}><PreviousJobs /></Suspense>} />
              
              <Route path="/vehicles" element={<Suspense fallback={<PageLoader />}><Vehicles /></Suspense>} />
              <Route path="/subcontractors" element={<Suspense fallback={<PageLoader />}><Subcontractors /></Suspense>} />
              <Route path="/subcontractors/:id" element={<Suspense fallback={<PageLoader />}><SubcontractorDetail /></Suspense>} />
              <Route path="/settings/automation" element={<Suspense fallback={<PageLoader />}><AutomationCentre /></Suspense>} />
              <Route path="/settings" element={<Suspense fallback={<PageLoader />}><Settings /></Suspense>} />
              <Route path="/subscription-status" element={<Suspense fallback={<PageLoader />}><SubscriptionStatus /></Suspense>} />
              <Route path="/subscriptions" element={<Suspense fallback={<PageLoader />}><Subscriptions /></Suspense>} />
              <Route path="/change-plan" element={<Suspense fallback={<PageLoader />}><ChangePlan /></Suspense>} />
              <Route path="/resume-job/:jobId" element={<Suspense fallback={<PageLoader />}><ResumeJob /></Suspense>} />
              <Route path="/job/:jobId" element={<Suspense fallback={<PageLoader />}><JobDetails /></Suspense>} />
              <Route path="/invoice/:jobId" element={<Suspense fallback={<PageLoader />}><InvoiceRoute /></Suspense>} />
              <Route path="/appraisal/:appraisalId" element={<Suspense fallback={<PageLoader />}><AppraisalViewer /></Suspense>} />
              <Route path="/ready-to-invoice" element={<Suspense fallback={<PageLoader />}><ReadyToInvoice /></Suspense>} />
              <Route path="/driver-settings" element={<Suspense fallback={<PageLoader />}><DriverSettings /></Suspense>} />
              <Route path="/help-guides" element={<Suspense fallback={<PageLoader />}><HelpGuides /></Suspense>} />
              <Route path="/setup-centre" element={<Suspense fallback={<PageLoader />}><SetupCentre /></Suspense>} />
              <Route path="/vehicle-daily-checks" element={<Suspense fallback={<PageLoader />}><VehicleDailyChecks /></Suspense>} />
              <Route path="/quick-quote" element={<Suspense fallback={<PageLoader />}><QuickQuote /></Suspense>} />
              <Route path="/quote-builder" element={<Suspense fallback={<PageLoader />}><NativeErrorBoundary fallbackTitle="Quote Builder crashed"><QuoteBuilder /></NativeErrorBoundary></Suspense>} />
              <Route path="/saved-quotes" element={<Suspense fallback={<PageLoader />}><NativeErrorBoundary fallbackTitle="Saved Quotes crashed"><SavedQuotes /></NativeErrorBoundary></Suspense>} />
              
              <Route path="/shortcuts" element={<Suspense fallback={<PageLoader />}><NativeShortcutsDashboard /></Suspense>} />
              <Route path="/offline-jobs" element={<Suspense fallback={<PageLoader />}><OfflineDriverJobs /></Suspense>} />
              <Route path="/offline-job/:jobId" element={<Suspense fallback={<PageLoader />}><OfflineJobDetail /></Suspense>} />
              <Route path="/pending-uploads" element={<Suspense fallback={<PageLoader />}><PendingUploads /></Suspense>} />
              <Route path="/more" element={<Suspense fallback={<PageLoader />}><More /></Suspense>} />
              <Route path="/integrations/fleethub-dev" element={<Suspense fallback={<PageLoader />}><FleetHubDev /></Suspense>} />
              <Route path="/integrations/fleethub-jobs-preview" element={import.meta.env.DEV ? <Suspense fallback={<PageLoader />}><FleetHubJobsPreview /></Suspense> : <Navigate to="/integrations/fleethub-dev" replace />} />
              <Route path="/integrations/fleethub-jobs" element={<Suspense fallback={<PageLoader />}><FleetHubJobs /></Suspense>} />
              <Route path="/feedback" element={<Suspense fallback={<PageLoader />}><Feedback /></Suspense>} />
              <Route path="/report-problem" element={<Suspense fallback={<PageLoader />}><ReportProblem /></Suspense>} />
              <Route path="/expenses" element={<Suspense fallback={<PageLoader />}><Expenses /></Suspense>} />
            </Route>

            {/* Standalone routes share the same legal footer. */}
            <Route element={<AppFooterLayout />}>
              <Route path="/super-admin" element={<SupportScopedRoute><Navigate to="/sa" replace /></SupportScopedRoute>} />
              <Route path="/super-admin-dashboard" element={<SupportScopedRoute><Navigate to="/sa" replace /></SupportScopedRoute>} />
              <Route path="/company-management" element={<SupportScopedRoute><ProtectedRoute><Suspense fallback={<PageLoader />}><CompanyManagement /></Suspense></ProtectedRoute></SupportScopedRoute>} />
              <Route path="/company/:companyId" element={<SupportScopedRoute><ProtectedRoute><Suspense fallback={<PageLoader />}><CompanyDetails /></Suspense></ProtectedRoute></SupportScopedRoute>} />
              <Route path="/admin/link-subscription" element={<SupportScopedRoute><ProtectedRoute><Suspense fallback={<PageLoader />}><LinkSubscription /></Suspense></ProtectedRoute></SupportScopedRoute>} />
              <Route path="/create-admin" element={<SupportScopedRoute><ProtectedRoute><Suspense fallback={<PageLoader />}><CreateAdminUser /></Suspense></ProtectedRoute></SupportScopedRoute>} />
              <Route path="/anchor-debug" element={<Suspense fallback={<PageLoader />}><AnchorDebugOverlay /></Suspense>} />
              <Route path="/anchor-calibration" element={<SupportScopedRoute><ProtectedRoute><Suspense fallback={<PageLoader />}><AnchorCalibration /></Suspense></ProtectedRoute></SupportScopedRoute>} />
              {/* Customer quote acceptance: token access, no account required. */}
              <Route path="/quote/:token" element={<Suspense fallback={<PageLoader />}><PublicQuote /></Suspense>} />
              <Route path="/signature" element={<Suspense fallback={<PageLoader />}><SignaturePage /></Suspense>} />
              <Route path="/track" element={<Suspense fallback={<PageLoader />}><TrackDelivery /></Suspense>} />
              <Route path="/subscribe" element={<Suspense fallback={<PageLoader />}><Subscribe /></Suspense>} />
              <Route path="/payment-success" element={<Suspense fallback={<PageLoader />}><PaymentSuccess /></Suspense>} />
              <Route path="/download" element={<Suspense fallback={<PageLoader />}><DownloadPage /></Suspense>} />
              <Route path="/accept-invite" element={<Suspense fallback={<PageLoader />}><AcceptInvite /></Suspense>} />
            </Route>
            
            {/* Native-only Super Admin Command Centre (web redirects to /sa) */}
            <Route path="/sa-mobile/*" element={<SupportScopedRoute><Suspense fallback={<PageLoader />}><SAMobileShell /></Suspense></SupportScopedRoute>} />

            {/* New Super Admin Routes /sa/* */}
            <Route path="/sa" element={<SupportScopedRoute><ProtectedRoute><SuperAdminLayout /></ProtectedRoute></SupportScopedRoute>}>
              {/* Overview */}
              <Route index element={<SAIndexRoute><SACEODashboard /></SAIndexRoute>} />
              <Route path="ceo-dashboard" element={<SACEODashboard />} />
              <Route path="dashboard" element={<SADashboard />} />
              <Route path="executive-intelligence" element={<SAExecutiveIntelligence />} />
              <Route path="live-tracking" element={<SALiveTracking />} />
              <Route path="control-tower" element={<SAControlTower />} />
              {/* Operations */}
              <Route path="companies" element={<SACompanies />} />
              <Route path="companies/:companyId" element={<SACompanyIntelligence />} />
              {/* Legacy direct link to the original company detail view */}
              <Route path="companies/:companyId/detail" element={<SACompanyDetail />} />
              <Route path="users" element={<SAUsers />} />
              <Route path="drivers" element={<SADrivers />} />
              <Route path="vehicles" element={<SAVehicles />} />
              <Route path="jobs" element={<SAJobs />} />
              <Route path="invoices" element={<SAInvoices />} />
              <Route path="batch-invoicing" element={<SABatchInvoicing />} />
              <Route path="problem-jobs" element={<SAProblemJobs />} />
              <Route path="appraisals" element={<SAAppraisals />} />
              <Route path="daily-checks" element={<SADailyChecks />} />
              {/* Platform */}
              <Route path="integrations" element={<SAIntegrations />} />
              <Route path="communications" element={<SACommunications />} />
              <Route path="outbox" element={<SAOutbox />} />
              <Route path="system-health" element={<SASystemHealth />} />
              <Route path="observability" element={<SAObservability />} />
              <Route path="storage" element={<SAStorage />} />
              <Route path="security" element={<SASecurity />} />
              {/* Intelligence */}
              <Route path="analytics" element={<SAAnalytics />} />
              <Route path="revenue" element={<SARevenue />} />
              <Route path="growth" element={<SAGrowth />} />
              <Route path="churn-risk" element={<SAChurnRisk />} />
              <Route path="cancellations" element={<SACancellations />} />
              <Route path="profit-loss" element={<SAProfitLoss />} />
              {/* AI Knowledge Engine */}
              <Route path="knowledge" element={<KnowledgeLayout />}>
                <Route index element={<KnowledgeDashboard />} />
                <Route path="topics" element={<KnowledgeTopics />} />
                <Route path="topics/:topicId" element={<KnowledgeTopicEditor />} />
                <Route path="graph" element={<KnowledgeGraph />} />
                <Route path="entities" element={<KnowledgeEntities />} />
                <Route path="entities/:entityId" element={<KnowledgeEntityEditor />} />
                <Route path="faqs" element={<KnowledgeFaqs />} />
                <Route path="assets" element={<KnowledgeAssets />} />
                <Route path="pages" element={<KnowledgePages />} />
                <Route path="strategist" element={<ContentStrategist />} />
                <Route path="research" element={<ResearchAgent />} />
                <Route path="research/:runId" element={<ResearchRunReview />} />
                <Route path="composer" element={<ContentComposer />} />
                <Route path="composer/:compositionId" element={<ContentComposerEditor />} />
                <Route path="categories" element={<KnowledgeCategoriesPage />} />
                <Route path="features" element={<KnowledgeFeaturesPage />} />
                <Route path="industries" element={<KnowledgeIndustriesPage />} />
                <Route path="competitors" element={<KnowledgeCompetitorsPage />} />
                <Route path="queue" element={<KnowledgeQueue />} />
                <Route path="settings" element={<KnowledgeSettings />} />
                <Route path="settings/providers" element={<AiProviders />} />
              </Route>
              {/* Admin */}
              <Route path="feature-flags" element={<SAFeatureFlags />} />
              <Route path="rate-limits" element={<SARateLimits />} />
              <Route path="maintenance" element={<SAMaintenance />} />
              <Route path="provider-config" element={<SAProviderConfig />} />
              <Route path="support-sessions" element={<SASupportSessions />} />
              <Route path="audit-logs" element={<SAAuditLogs />} />
              <Route path="activity-log" element={<SAActivityLog />} />
              <Route path="sessions" element={<SASessions />} />
              <Route path="alerts" element={<SAOperationalAlerts />} />
              <Route path="demo" element={<SADemo />} />
              <Route path="settings" element={<SASettings />} />
            </Route>
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE.
                The catch-all renders inside MarketingLayout so an unknown URL keeps
                the standard header/footer instead of a bare 404 screen. */}
            <Route element={<MarketingLayout />}>
              <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
            </Route>
          </Routes>
        </Suspense>
        </NativeErrorBoundary>
        </SupportSessionProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
    </BackendHealthProvider>
  </QueryClientProvider>
);

export default App;
